import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Survey, SurveyQuestion, SurveyQuestionType, SurveyStatus } from '../../types';
import { API_BASE_URL } from '../../config/api';
import { ADMIN_HOST } from '../../config/hosts';
import { excerpt, surveyAnswerColor, surveySettingLines, surveyStatusMeta } from './surveyUtils';

const STATUS_FILTERS: Array<{ value: SurveyStatus | 'current'; label: string }> = [
  { value: 'current', label: 'Current' },
  { value: 'active', label: 'Active' },
  { value: 'planning', label: 'Planning' },
  { value: 'closed', label: 'Closed' },
];

interface AnalyticsSurvey {
  _id: string;
  name: string;
  questionIds: string[];
}

interface AnalyticsAnswer {
  surveyId: string;
  surveyName: string;
  questionId: string;
  prompt: string;
  type: SurveyQuestionType;
  value: string;
  completedAt: string | Date;
}

interface AnalyticsPayload {
  surveys: AnalyticsSurvey[];
  questions: SurveyQuestion[];
  answers: AnalyticsAnswer[];
}

type TimeGroup = 'day' | 'week' | 'month';

const startOfDay = (value: Date) => {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date;
};

const startOfWeek = (value: Date) => {
  const date = startOfDay(value);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  date.setDate(diff);
  return date;
};

const startOfMonth = (value: Date) => {
  const date = startOfDay(value);
  date.setDate(1);
  return date;
};

const localDayKey = (value: Date) => {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatBucket = (key: string, group: TimeGroup) => {
  const date = new Date(`${key}T00:00:00`);
  if (Number.isNaN(date.getTime())) return key;
  if (group === 'month') {
    return date.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
  }
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const formatAnswer = (value: string, type?: SurveyQuestionType) => {
  if (type === 'boolean') {
    if (value === 'yes') return 'Yes';
    if (value === 'no') return 'No';
  }
  return value;
};

const StoreDashboardSurveysView: React.FC = () => {
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [analytics, setAnalytics] = useState<AnalyticsPayload | null>(null);
  const [filter, setFilter] = useState<SurveyStatus | 'current'>('current');
  const [surveyId, setSurveyId] = useState('');
  const [questionId, setQuestionId] = useState('');
  const [timeGroup, setTimeGroup] = useState<TimeGroup>('day');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [listResponse, analyticsResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/store-dashboard/surveys`, { withCredentials: true }),
        axios.get(`${API_BASE_URL}/store-dashboard/surveys/analytics`, { withCredentials: true }),
      ]);
      setSurveys(listResponse.data.surveys || []);
      setAnalytics({
        surveys: analyticsResponse.data.surveys || [],
        questions: analyticsResponse.data.questions || [],
        answers: analyticsResponse.data.answers || [],
      });
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load surveys');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const questionsForFilter = useMemo(() => {
    const questions = analytics?.questions || [];
    if (!surveyId) {
      const answeredIds = new Set((analytics?.answers || []).map((answer) => answer.questionId));
      return questions.filter((question) => question._id && answeredIds.has(question._id));
    }
    const selected = analytics?.surveys.find((survey) => survey._id === surveyId);
    const allowed = new Set(selected?.questionIds || []);
    return questions.filter((question) => question._id && allowed.has(question._id));
  }, [analytics, surveyId]);

  useEffect(() => {
    if (!questionsForFilter.length) {
      if (questionId) setQuestionId('');
      return;
    }
    if (!questionId || !questionsForFilter.some((question) => question._id === questionId)) {
      setQuestionId(questionsForFilter[0]._id || '');
    }
  }, [questionsForFilter, questionId]);

  const selectedQuestion = questionsForFilter.find((question) => question._id === questionId);
  const filteredAnswers = useMemo(() => {
    return (analytics?.answers || []).filter((answer) => {
      if (surveyId && answer.surveyId !== surveyId) return false;
      if (questionId && answer.questionId !== questionId) return false;
      return true;
    });
  }, [analytics, questionId, surveyId]);

  const values = useMemo(() => {
    const seen: string[] = [];
    filteredAnswers.forEach((answer) => {
      const label = formatAnswer(answer.value, selectedQuestion?.type);
      if (!seen.includes(label)) seen.push(label);
    });
    return seen;
  }, [filteredAnswers, selectedQuestion?.type]);

  const timeSeries = useMemo(() => {
    const buckets = new Map<string, Record<string, number | string>>();
    filteredAnswers.forEach((answer) => {
      const date = new Date(answer.completedAt);
      if (Number.isNaN(date.getTime())) return;
      const start = timeGroup === 'month'
        ? startOfMonth(date)
        : timeGroup === 'week'
          ? startOfWeek(date)
          : startOfDay(date);
      const key = localDayKey(start);
      const label = formatAnswer(answer.value, selectedQuestion?.type);
      const current = buckets.get(key) || { bucket: key, label: formatBucket(key, timeGroup) };
      current[label] = Number(current[label] || 0) + 1;
      buckets.set(key, current);
    });
    return Array.from(buckets.values())
      .sort((a, b) => String(a.bucket).localeCompare(String(b.bucket)))
      .map((row) => {
        const filled: Record<string, number | string> = { ...row };
        values.forEach((value) => {
          if (filled[value] === undefined) filled[value] = 0;
        });
        return filled;
      });
  }, [filteredAnswers, selectedQuestion?.type, timeGroup, values]);

  const visible = surveys.filter((survey) => (
    filter === 'current' ? survey.status !== 'closed' : survey.status === filter
  ));
  const isText = selectedQuestion?.type === 'text';
  const answerCount = filteredAnswers.length;
  const surveyCount = new Set(filteredAnswers.map((answer) => answer.surveyId)).size;

  return (
    <div className="store-dash">
      <div className="store-dash-toolbar">
        <div>
          <div className="store-dash-kicker">In-app</div>
          <h1>Surveys</h1>
        </div>
        <div className="store-dash-ranges">
          {STATUS_FILTERS.map((item) => (
            <button
              key={item.value}
              type="button"
              className={`store-dash-range${filter === item.value ? ' is-active' : ''}`}
              onClick={() => setFilter(item.value)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : error ? (
        <div className="alert alert-danger">{error}</div>
      ) : (
        <>
          <div className="store-dash-selects">
            <label>
              Survey
              <select value={surveyId} onChange={(event) => setSurveyId(event.target.value)}>
                <option value="">All surveys</option>
                {(analytics?.surveys || []).map((survey) => (
                  <option key={survey._id} value={survey._id}>{survey.name}</option>
                ))}
              </select>
            </label>
            <label>
              Question
              <select value={questionId} onChange={(event) => setQuestionId(event.target.value)}>
                {questionsForFilter.length === 0 && <option value="">No questions with answers</option>}
                {questionsForFilter.map((question) => (
                  <option key={question._id} value={question._id}>
                    {question.prompt}
                    {question.usedOnSurveyCount && question.usedOnSurveyCount > 1
                      ? ` (${question.usedOnSurveyCount} surveys)`
                      : ''}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Group by
              <select value={timeGroup} onChange={(event) => setTimeGroup(event.target.value as TimeGroup)}>
                <option value="day">Day</option>
                <option value="week">Week</option>
                <option value="month">Month</option>
              </select>
            </label>
          </div>

          <div className="store-dash-cards">
            <div className="store-dash-card">
              <span>Answers</span>
              <strong>{answerCount.toLocaleString()}</strong>
            </div>
            <div className="store-dash-card">
              <span>Surveys</span>
              <strong>{surveyCount.toLocaleString()}</strong>
            </div>
            <div className="store-dash-card">
              <span>Question</span>
              <strong className="store-dash-card-text">{selectedQuestion?.prompt || '—'}</strong>
            </div>
          </div>

          <section className="store-dash-chart">
            <h2>{isText ? 'Written answers over time' : 'Answers over time'}</h2>
            {!selectedQuestion || answerCount === 0 ? (
              <p className="store-dash-hint">No answers match these filters.</p>
            ) : isText ? (
              <div className="store-dash-table-wrap">
                <table className="store-dash-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Survey</th>
                      <th>Answer</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...filteredAnswers].reverse().map((answer, index) => (
                      <tr key={`${answer.surveyId}-${answer.completedAt}-${index}`}>
                        <td>{new Date(answer.completedAt).toLocaleDateString()}</td>
                        <td>{answer.surveyName}</td>
                        <td>{answer.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={timeSeries}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="label" minTickGap={24} />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  {values.map((value, index) => (
                    <Line
                      key={value}
                      type="monotone"
                      dataKey={value}
                      stroke={surveyAnswerColor(value, index)}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            )}
          </section>

          {visible.length === 0 ? (
            <p className="store-dash-hint">
              {filter === 'current' ? 'No active or planning surveys right now.' : 'No surveys in this status.'}
            </p>
          ) : (
            <div className="store-dash-surveys">
              {visible.map((survey) => {
                const meta = surveyStatusMeta(survey.status);
                const settings = surveySettingLines(survey);
                const summary = excerpt(survey.summary);
                return (
                  <article key={survey._id} className="store-dash-survey">
                    <div className="store-dash-survey-top">
                      <strong>{survey.name}</strong>
                      <span className={`store-dash-survey-status is-${survey.status}`}>{meta.label}</span>
                    </div>
                    {summary && <p>{summary}</p>}
                    <div className="store-dash-survey-meta">
                      <span>Who: {settings.who.join(', ') || '—'}</span>
                      <span>Devices: {settings.devices.join(', ') || 'All Devices'}</span>
                      <span>Platform: {settings.platforms.join(', ') || 'iOS & Android'}</span>
                      <span>When: {settings.when.join(' · ')}</span>
                      <span>Rollout: {settings.rollout.join(', ')}</span>
                    </div>
                    <div className="store-dash-survey-stats">
                      <span>{survey.questionCount ?? survey.questions?.length ?? 0} questions</span>
                      <span>{survey.resultCount || 0} responses</span>
                      <span>{survey.optOutCount || 0} opted out</span>
                      <button type="button" onClick={() => setSurveyId(survey._id)}>
                        Show answers
                      </button>
                      <a href={`https://${ADMIN_HOST}/surveys/${survey._id}`} target="_blank" rel="noreferrer">
                        Open in admin
                      </a>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default StoreDashboardSurveysView;
